import {useRef, useState} from 'react'
import {Plus, Trash2, ChevronUp, ChevronDown, Loader2} from 'lucide-react'
import type {Board, BoardColumn} from '../../types'
import {useApp} from '../../context/AppContext'
import {generateId} from '../../utils/helpers'
import Modal from '../common/Modal'

interface ColumnManagerProps {
  board: Board
  onClose: () => void
}

export default function ColumnManager({board, onClose}: ColumnManagerProps) {
  const {apiUpdateBoardColumns, state} = useApp()
  const [columns, setColumns] = useState<BoardColumn[]>(() =>
    board.columns.map((column) => ({...column}))
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const savingRef = useRef(false)
  const countTasks = (status: string) =>
    state.tasks.filter((task) => task.boardId === board.id && task.status === status).length

  const updateColumn = (index: number, changes: Partial<BoardColumn>) => {
    setColumns((previous) =>
      previous.map((column, i) => (i === index ? {...column, ...changes} : column))
    )
    setError('')
  }

  const moveColumn = (index: number, direction: number) => {
    const next = index + direction
    if (next < 0 || next >= columns.length) return
    setColumns((previous) => {
      const updated = [...previous]
      ;[updated[index], updated[next]] = [updated[next], updated[index]]
      return updated
    })
  }

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault()
    if (savingRef.current) return
    const normalized = columns.map((column) => ({...column, title: column.title.trim()}))
    if (!normalized.length || normalized.some((column) => !column.title)) {
      setError('Mantenha pelo menos uma coluna e dê um nome a cada etapa.')
      return
    }
    if (
      new Set(normalized.map((column) => column.title.toLocaleLowerCase('pt-BR'))).size !==
      normalized.length
    ) {
      setError('Use nomes diferentes para identificar cada etapa do quadro.')
      return
    }
    if (
      normalized.some(
        (column) =>
          column.limit !== undefined && (!Number.isInteger(column.limit) || column.limit < 1)
      )
    ) {
      setError('O limite deve ser um número inteiro maior que zero. Deixe vazio para não limitar.')
      return
    }
    if (
      board.columns.some(
        (column) =>
          !normalized.some((next) => next.status === column.status) && countTasks(column.status) > 0
      )
    ) {
      setError(
        'Uma coluna removida ainda possui tarefas. Mova as tarefas antes de remover a etapa.'
      )
      return
    }
    savingRef.current = true
    setSaving(true)
    setError('')
    try {
      await apiUpdateBoardColumns(board.id, normalized)
      onClose()
    } catch {
      setError(
        'Não foi possível salvar as colunas. Suas alterações foram mantidas para tentar novamente.'
      )
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title='Organizar colunas'
      size='lg'
      closeDisabled={saving}
      description='Defina as etapas, a ordem e o limite de tarefas em andamento.'
    >
      <form onSubmit={handleSave}>
        <fieldset disabled={saving} className='min-w-0 p-4 sm:p-6'>
          <p className='mb-4 rounded-xl bg-brand-50 px-4 py-3 text-xs leading-relaxed text-brand-700'>
            Renomear uma coluna mantém suas tarefas. Para remover uma etapa, mova antes as tarefas
            para outra coluna. O limite sinaliza excesso de trabalho sem impedir movimentações.
          </p>
          <div className='space-y-3'>
            {columns.map((column, index) => {
              const taskCount = countTasks(column.status)
              const canRemove = columns.length > 1 && taskCount === 0
              return (
                <div
                  key={column.id}
                  className='rounded-xl border border-slate-200 bg-slate-50/60 p-3'
                >
                  <div className='mb-2 flex items-center justify-between gap-2'>
                    <span className='text-xs font-medium text-slate-500'>
                      Etapa {index + 1} · {taskCount} {taskCount === 1 ? 'tarefa' : 'tarefas'}
                    </span>
                    <div className='flex items-center gap-1'>
                      <button
                        type='button'
                        onClick={() => moveColumn(index, -1)}
                        disabled={index === 0}
                        className='devops-icon-button disabled:opacity-30'
                        aria-label={`Mover ${column.title || 'coluna'} para cima`}
                      >
                        <ChevronUp size={15} />
                      </button>
                      <button
                        type='button'
                        onClick={() => moveColumn(index, 1)}
                        disabled={index === columns.length - 1}
                        className='devops-icon-button disabled:opacity-30'
                        aria-label={`Mover ${column.title || 'coluna'} para baixo`}
                      >
                        <ChevronDown size={15} />
                      </button>
                      <button
                        type='button'
                        onClick={() => {
                          if (canRemove) setColumns(columns.filter((_, i) => i !== index))
                        }}
                        disabled={!canRemove}
                        title={
                          taskCount
                            ? 'Mova as tarefas antes de remover esta coluna'
                            : columns.length === 1
                            ? 'O quadro precisa de pelo menos uma coluna'
                            : 'Remover coluna'
                        }
                        className='devops-icon-button hover:text-red-600 disabled:opacity-30'
                        aria-label={`Remover ${column.title || 'coluna'}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                  <div className='grid grid-cols-1 gap-3 sm:grid-cols-[1fr_120px_60px]'>
                    <label className='space-y-1 text-xs font-medium text-slate-600'>
                      <span>Nome da etapa</span>
                      <input
                        type='text'
                        required
                        maxLength={80}
                        value={column.title}
                        onChange={(event) => updateColumn(index, {title: event.target.value})}
                        className='w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm'
                        placeholder='Ex.: Em revisão'
                      />
                    </label>
                    <label className='space-y-1 text-xs font-medium text-slate-600'>
                      <span>Limite de tarefas</span>
                      <input
                        type='number'
                        min={1}
                        step={1}
                        value={column.limit ?? ''}
                        onChange={(event) =>
                          updateColumn(index, {
                            limit:
                              event.target.value === '' ? undefined : Number(event.target.value),
                          })
                        }
                        placeholder='Sem limite'
                        className='w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm'
                      />
                    </label>
                    <label className='space-y-1 text-xs font-medium text-slate-600'>
                      <span>Cor</span>
                      <input
                        type='color'
                        value={column.color}
                        onChange={(event) => updateColumn(index, {color: event.target.value})}
                        className='block h-10 w-full cursor-pointer rounded-lg border border-slate-200 bg-white p-1'
                      />
                    </label>
                  </div>
                </div>
              )
            })}
          </div>
          <button
            type='button'
            onClick={() => {
              const id = 'col-' + generateId()
              setColumns([...columns, {id, title: '', status: id, color: '#94a3b8'}])
            }}
            className='mt-4 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 px-4 py-3 text-sm font-medium text-slate-600 hover:border-brand-400 hover:text-brand-600'
          >
            <Plus size={16} />
            Adicionar etapa
          </button>
          {error && (
            <p role='alert' className='mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700'>
              {error}
            </p>
          )}
        </fieldset>
        <div className='sticky bottom-0 flex justify-end gap-3 border-t border-slate-100 bg-white px-4 py-4 sm:px-6'>
          <button
            type='button'
            onClick={onClose}
            disabled={saving}
            className='devops-secondary-button disabled:opacity-40'
          >
            Cancelar
          </button>
          <button
            type='submit'
            disabled={saving}
            className='devops-primary-button disabled:opacity-50'
          >
            {saving && <Loader2 size={16} className='animate-spin' />}
            {saving ? 'Salvando...' : 'Salvar colunas'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
